import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { rewriteAccountLinksForSoftLogin } from "@utils/account";
import { attemptMethod } from "@utils/general";
import { useIsClientSide } from "@utils/hooks/isClient.hook";

import { SoftLoginService } from "@services/softLogin.service";

import { useThunkDispatch } from "@redux/dispatcher";
import { load } from "@redux/modules/api/data/customer/customer.module";
import { getCustomer } from "@redux/modules/api/data/customer/customer.selectors";
import { getHeader } from "@redux/modules/value/header/header.selectors";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import AccountButtonMenu from "@components/Account/AccountButton/accountButtonMenu.component";
import { HeaderButton } from "@components/General/HeaderButton/headerButton.component";
import { PageLinkProps } from "@components/General/PageLinks/PageLink/pageLink.component";

export type AccountButtonContainerProps = {
    children?: React.ReactNode;
    icon: string;
    text: string;
    hover?: boolean;
    dropdown?: boolean;
    alerts?: number;
    invert?: boolean;
    link?: string;
    redirect?: string;
    menuLinksSettings?: string;
    ariaLabel?: string;
};

const AccountButtonContainer = ({
    icon,
    text,
    hover,
    alerts,
    invert,
    link,
    redirect,
    menuLinksSettings,
    ariaLabel,
}: AccountButtonContainerProps) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();
    const header = useSelector(getHeader);
    const customer = useSelector(getCustomer);
    const isClientSide = useIsClientSide();
    const [parsedMenuLinks, setParsedMenuLinks] =
        useState<Array<PageLinkProps>>();

    useEffect(() => {
        if (isClientSide) {
            dispatch(load());
        }
    }, [isClientSide]);

    useEffect(() => {
        const isLoggedIn = !!(customer && !customer.guest);

        if (menuLinksSettings) {
            setParsedMenuLinks(
                rewriteAccountLinksForSoftLogin(
                    isLoggedIn,
                    attemptMethod(
                        () => JSON.parse(menuLinksSettings),
                        undefined,
                    ),
                    link,
                ),
            );
        }
    }, [customer]);

    let firstName = undefined;
    let isCustomer = false;
    let userIsLoggedIn = false;

    if (isClientSide) {
        firstName = SoftLoginService.getSoftLoginDetails().firstName;
        isCustomer = !!(customer && !customer.guest);
        userIsLoggedIn = isCustomer || SoftLoginService.isSoftLoggedIn();

        if (isCustomer) {
            firstName = customer?.firstName;
        }
    }

    const componentShouldInvert = isClientSide
        ? invert || header?.mobile
        : false;

    return (
        <HeaderButton
            icon={icon}
            hover={hover}
            alerts={alerts}
            invert={componentShouldInvert}
            ariaLabel={ariaLabel}
            text={
                firstName
                    ? t("general.header.account.welcome", firstName)
                    : text
            }
            link={isCustomer ? redirect : link}
        >
            {userIsLoggedIn && <AccountButtonMenu links={parsedMenuLinks} />}
        </HeaderButton>
    );
};
export { AccountButtonContainer };
